import { identity } from '../chronicles-ui/identity';

const publicRoute = route => /^\/event(?:\/[^/]+)?$/.test(route);
export const request = async route => (await identity.request(route, { authenticated: !publicRoute(route) })).data;
const post = async (route, data) => (await identity.request(route, { method: 'post', data })).data;
const put = async (route, data) => identity.request(route, { method: 'put', data });
const remove = async route => identity.request(route, { method: 'delete' });
export const updateCharacter = (characterId, data) => put(`/character/${characterId}`, data);

// ===== Characters =====

export const fetchCharacter = async (characterId) => {
    const characterData = await request(`/character/${characterId}`);
    return characterData;
};

export const fetchCharacters = async () => {
    return await request('/character');
};

// Characters belonging to a specific player (scoped the same way the sidebar is).
export const fetchCharactersByPlayer = async (playerId) => {
    return await request(`/character?playerId=${playerId}`);
};

// SP award/spend history for the character's player, newest event first.
export const fetchCharacterSpLog = async (characterId) => {
    return await request(`/character/${characterId}/sp-log`);
};

// New: create a character (expects your API POST /character to accept { name } and set defaults)
export const createCharacter = async ({ name, ...rest } = {}) => {
    if (!name) throw new Error('createCharacter requires a name');
    return await post('/character', { name, ...rest });
};

// ===== Knowledges =====

export const fetchKnowledges = async (playerId) => {
    return await request(`/knowledge`);
};

// ===== Players =====

export const fetchPlayer = async (playerId) => {
    return await request(`/player/${playerId}`);
};

export const fetchAllPlayers = async () => {
    return await request('/player');
};

// Staff-only: pin which of a player's characters is Primary/Secondary.
// Pass null for either field to clear that override and revert to the
// characterId-ascending default.
export const updateCharacterAssignments = async (playerId, { primaryCharacterId, secondaryCharacterId }) => {
    const res = await put(
        `/player/${playerId}/character-assignments`,
        { primaryCharacterId, secondaryCharacterId }
    );
    return res.data;
};

export const fetchCharactersForPlayer = async (playerId) => {
    return await request(`/character?playerId=${playerId}`);
};

// ===== Races / Skillsets =====

export const fetchRaces = async () => {
    return request('/races');
};

export const fetchSkillsets = async () => {
    return request('/skillset');
};

// ===== Skills =====

/**
 *
 * @param filters
 * @param filters.characterId
 * @returns {Promise<any|undefined>}
 */
export const fetchSkills = async (filters = {}) => {
    if (filters.characterId) {
        return await request(`/skill?characterId=${filters.characterId}`);
    }
};

// ===== Transforms =====

export const fetchTransforms = async () => {
    return await request('/transforms');
};

export const fetchTransform = async (transformId) => {
    return await request(`/transforms/${transformId}`);
};

export const createTransform = async (payload) => {
    return await post('/transforms', payload);
};

export const updateTransform = async (transformId, payload) => {
    const res = await put(`/transforms/${transformId}`, payload);
    return res.data;
};

export const deleteTransform = async (transformId) => {
    const res = await remove(`/transforms/${transformId}`);
    return res.data;
};

// ===== Abilities (shared pool of skillset-8 skills) =====

export const fetchAbilities = async () => {
    return await request('/transforms/abilities');
};

export const createAbility = async ({ name, notes }) => {
    return await post('/transforms/abilities', { name, notes }); // { skillId, name, notes }
};

// Updates the shared ability for every transform that references it.
export const updateAbility = async (skillId, { name, notes }) => {
    const res = await put(`/transforms/abilities/${skillId}`, { name, notes });
    return res.data; // { skillId, name, notes }
};

// ===== Events =====

export const fetchEvents = async () => {
    return await request('/event');
};

export const fetchEvent = async (eventId) => {
    return await request(`/event/${eventId}`);
};

export const createEvent = async (data) => {
    return await post('/event', data);
};

export const updateEvent = async (eventId, data) => {
    const res = await put(`/event/${eventId}`, data);
    return res.data;
};

export const deleteEvent = async (eventId) => {
    await remove(`/event/${eventId}`);
};

// ===== Attendance =====

export const fetchAttendance = async (eventId) => {
    return await request(`/event/${eventId}/attendance`);
};

export const fetchAttendanceRecord = async (eventId, playerId) => {
    return await request(`/event/${eventId}/attendance/${playerId}`);
};

export const upsertAttendance = async (eventId, playerId, data) => {
    const res = await put(`/event/${eventId}/attendance/${playerId}`, data);
    return res.data;
};

export const deleteAttendance = async (eventId, playerId) => {
    await remove(`/event/${eventId}/attendance/${playerId}`);
};

export const finalizeAttendance = async (eventId, playerId) => {
    return await post(`/event/${eventId}/attendance/${playerId}/finalize`, {});
};

// ===== Event checkout scans =====

export const submitCheckoutScan = async ({ eventId, qrPayload, imageBase64, imageMimeType }) => {
    return await post('/event-scans', { eventId, qrPayload, imageBase64, imageMimeType });
};

export const fetchCheckoutScansForEvent = async (eventId) => {
    return await request(`/event-scans/event/${eventId}`);
};

export const fetchCheckoutScanDetail = async (eventScanId) => {
    return await request(`/event-scans/${eventScanId}`);
};

// Fetches the scanned sheet photo as an object URL (the <img> tag can't send
// an Authorization header itself, so the blob is fetched here instead).
// Uses the downscaled thumbnail to save on data transfer; use
// fetchCheckoutScanFullImageUrl for the full-size version (e.g. in a modal).
export const fetchCheckoutScanImageUrl = async (eventScanId) => {
    const res = await identity.request(`/event-scans/${eventScanId}/image/thumbnail`, {
        responseType: 'blob',
    });
    return URL.createObjectURL(res.data);
};

export const fetchCheckoutScanFullImageUrl = async (eventScanId) => {
    const res = await identity.request(`/event-scans/${eventScanId}/image`, {
        responseType: 'blob',
    });
    return URL.createObjectURL(res.data);
};

export const markCheckoutScanProcessed = async (eventScanId) => {
    return await post(`/event-scans/${eventScanId}/mark-processed`, {});
};

// ===== QR codes =====
// The printed character sheet's QR code only encodes a qrCodeId (a UUID).
// The actual build payload is stored server-side and resolved by id here.

export const createQrCode = async ({ characterId, payload }) => {
    return await post('/qrCode', { characterId, payload });
};

export const fetchQrCode = async (qrCodeId) => {
    return await request(`/qrCode/${qrCodeId}`);
};
